import { Fragment, useEffect, useState } from "react";
import {
    Select,
    Button,
    Typography,
    FormHelperText,
    MenuItem,
    Checkbox,
    Radio,
    Grid,
    Tabs,
    Tab,
    Menu,
    Switch,
} from "@mui/material";
import { tss } from "tss-react/mui";
import { SketchPicker } from "react-color";
import * as GlobalActions from "actions/GlobalActions";
import { ViewerActions } from "actions";
import * as Utils from "services/UtilsService";
import LayersActions from "actions/LayersActions";
import {
    DashInput,
    Histogram,
    HorizontalColorPalette,
    ColorGradient,
    CustomSelect,
    Section,
    Switcher,
    ColorPalette,
} from "components";
import { ColorLegendDataMethods } from "constants/viewer";
import { DefaultColorPalette } from "constants/DefaultColorPalette";
import GradientActions from "actions/GradientActions";
import { handleLayerEvent } from "actions/ViewerTools";
import commonStyles from "styles/common";

export const useStylesColorLegend = tss.create(({ theme }) => ({
    sectionContainer: {
        borderTop: "solid 1px #f0f0f0",
        lineHeight: 0,
    },
    subSectionContainer: {
        marginTop: 0,
        marginLeft: 5,
        marginRight: 5,
        marginBottom: 5,
    },
    attributeRow: {
        marginTop: 0,
        marginBottom: 0,
    },
    sectionRow: {
        marginTop: 5,
        marginBottom: 0,
    },
    sectionGradient: {
        marginTop: 10,
        marginBottom: 10,
    },
    sectionLabel: {
        margin: 0,
        marginLeft: -5,
        display: "flex",
        alignItems: "center",
        fontWeight: "bold",
        fontSize: 14,
        color: theme.palette.text.secondary, //"#283C50",
        width: 280,
    },
    label: {
        fontSize: 14,
        color: theme.palette.text.secondary, //"#283C50",
        letterSpacing: 0,
        lineHeight: "16px",
    },
    smallLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary, //"#687684",
        letterSpacing: 0.21,
        lineHeight: "14px",
        whiteSpace: "nowrap",
        width: 136,
    },
    subSectionLabel: {
        fontSize: 14,
        color: theme.palette.text.secondary, //"#283C50",
        letterSpacing: 0,
        lineHeight: "16px",
    },
    inputLabel: {
        fontSize: 14,
        marginTop: 4,
        marginBottom: 7,
    },
    hintLabel: {
        marginTop: 10,
        marginBottom: 7,
        fontSize: 12,
         color: theme.palette.text.secondary, //"color: "rgba(0,0,0,0.55)",
    },
    modeSelector: {
        marginBottom: 10,
    },
    buttonColorMode: {
        width: "50%",
        height: 26,
        margin: "0px 0px 0 0px",
        fontSize: 10,
        fontWeight: "bold",
        boxShadow: "none",
        letterSpacing: "0.84px",
        lineHeight: "11px",
        textAlign: "center",
    },
    disableColorMode: {
        width: "50%",
        height: 26,
        //backgroundColor: "rgba(40,60,80,0.2)",
        color: theme.palette.text.disabled, //"#687684",
        fontSize: 10,
        fontWeight: "bold",
        letterSpacing: "0.84px",
        lineHeight: "11px",
        textAlign: "center",
    },
    layerInput: {
        width: 50,
        outline: 0,
        border: 0,
        borderBottom: "solid 1px #E6E6E6",
        textAlign: "right",
        padding: "5px 5px",
    },
    numberInput: {
        textAlign: "right",
        fontSize: 14,
        marginTop: 0,
        color: theme.palette.text.secondary
    },
    numberHint: {
        textAlign: "left",
    },
    layerAttributeInput: {
        width: "calc(100% - 3px)",
        outline: 0,
        border: 0,
        borderBottom: "solid 1px #E6E6E6",
        textAlign: "right",
        padding: "5px 5px",
        marginTop: 4,
    },
    bottomButtonContainer: {
        marginBottom: 0,
        marginTop: 5,
        textAlign: "right",
    },
    booleanPill: {
        margin: 0,
        marginTop: 5,
        marginLeft: 5,
        overflow: "visible",
        borderRadius: 16,
        fontSize: 12,
        height: 20,
        minWidth: 0,
        width: 40,
        textTransform: "none",
        backgroundColor: "rgba(0,39,118,0.08)",
    },
    staticColorBlock: {
        width: 16,
        height: 16,
        margin: 0,
        padding: 0,
    },
    staticColorBlockSelected: {
        width: 16,
        height: 16,
        margin: 0,
        padding: 0,
        border: "solid 1px orange",
    },
    drillHoleSideAttributeListLeft: {
        width: 120,
        float: "left",
    },
    drillHoleSideAttributeListRight: {
        width: 120,
        float: "right",
    },
    drillHoleCenterAttributeList: {
        width: 20,
    },
    colorContainer: {
        marginLeft: 7,
    },
    showMore: {
        height: 14,
        width: 241,
        color: "#0075B0",
        fontSize: 12,
        letterSpacing: "0.4px",
        position: "relative",
        top: 20,
        left: 10,
        marginBottom: 10,
        cursor: "pointer",
    },
    arrow: {
        width: 16,
        height: 16,
        position: "relative",
        left: 4,
    },
    listItem: {
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "1px 1px black",
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
    select: {
        width: 270,
        height: 40,
        display: "inline-flex",
        alignItems: "center",
        textOverflow: "ellipsis",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        cursor: "default",
        color: theme.palette.text.secondary
    },
    gradientImage: {
        transform: "rotate(90deg)",
        width: "300px",
        height: "300px",
    },
    gradientLabel: {
        marginTop: 1,
        overflowX: "hidden",
        textOverflow: "ellipsis",
        fontSize: 12,
    },
    gradientImageContainer: {
        height: 30,
        overflow: "hidden",
    },
    row: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        height: 40,
        width: "100%",
    },
    twoSided: {
        display: "inline-flex",
        alignItems: "center",
        position: "absolute",
        right: 10,
        marginTop: 5,
    },
    picker: {
        width: "250px !important",
        boxShadow: "none !important",
        " & div > div > div > label ": {
            paddingTop: "8px !important",
        },
    },
    content: {
        padding: 10,
    },
}));

const globalSettings: GlobalActions.ColorLegendType[] = [];

const {
    getAttributesObjects,
    getColorLegendSettings,
    isLimboState,
    getCommonAttributeValue,
    getGlobalSettings,
    getMeshProperties,
    updateMeshProperties,
    getDataMinMaxValue,
} = GlobalActions;

type IColorLegend = {
    layer: any;
    selectedLayers: any;
    currentProject: any;
    attributeQueryEntity: string;
    getGradientImage: (gradientId: string) => "";
    updateParent: () => void;
};
interface IExtended extends IColorLegend {
    setNeedsApply: any;
    globalSettings: any;
    updateDataDrivenColor: any;
    onColorClick?: any;
    histogram?: any;
    backColor?: any;
    frontColor?: any;
    onDoubleSided?: any;
}

function NumericAttributes(props: IExtended) {
    const { classes, cx } = useStylesColorLegend();
    const { classes: common } = commonStyles();

    const {
        layer,
        selectedLayers,
        globalSettings,
        updateParent,
        updateDataDrivenColor,
        currentProject,
        attributeQueryEntity,
        backColor,
        histogram,
        onColorClick,
        onDoubleSided,
        setNeedsApply,
    } = props;

    const { requiresServerUpdate, isLimboState } = GlobalActions;

    var data: any = LayersActions.data[layer.ID];
    if (!data) {
        const entityId = GlobalActions.getEntityId(layer);
        if (entityId) {
            data = ViewerActions.extendedMeta[entityId];
        }
    }

    console.log("====>colorLegend.data:", data);
    const [update, setUpdate] = useState(false);
    const [colorGradientMenu, setColorGradientMenu] = useState(false);

    const {
        colorRangeMin,
        colorRangeMax,
        colorRangeStep,
        colorRangeCount,
        colorGradientId,
        colorAttributeIndex,
        doubleSided,
        dataDrivenMethod,
        dataDrivenEqualMode,
        reverseGradient,
        interpolateGradient,
        minMaxDynamic,
        minMaxData,
    } = globalSettings[layer.ID];

    var colorDataMin: number = 0;
    var colorDataMax: number = 0;
    if (data && colorAttributeIndex > -1) {
        const minMax: any = getDataMinMaxValue(data, colorAttributeIndex);
        if (minMax.minOK) {
            colorDataMin = Utils.round2Decimals(parseFloat(minMax.min));
        }
        if (minMax.maxOk) {
            colorDataMax = Utils.round2Decimals(parseFloat(minMax.max));
        }
    }

    const isMultiple = selectedLayers.length > 1;

    const setMinMax = () => {
        if (data) {
            const rangeMin: string = data["attribute" + colorAttributeIndex + "_bound_min"];
            const rangeMax: string = data["attribute" + colorAttributeIndex + "_bound_max"];

            if (typeof rangeMin !== "undefined") {
                setColorRangeMin(parseFloat(rangeMin));
            }
            if (typeof rangeMax !== "undefined") {
                setColorRangeMax(parseFloat(rangeMax), false);
            }
        }
    };

    const makeDirty = () => {
        if (requiresServerUpdate(attributeQueryEntity, currentProject)) {
            setNeedsApply(true);
            return false;
        } else return false;
    };

    if (minMaxData) {
        setMinMax();
    }

    function changeStats(evt: any) {
        var value = parseInt(evt.target.value);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["color_legend_data_driven_method"] = value;
                if (value > 0 && !currentLayer["color_legend_range_count"]) {
                    currentLayer["color_legend_range_count"] = 10;
                }
            });
        } else {
            layer["color_legend_data_driven_method"] = value;
            if (value > 0 && !layer["color_legend_range_count"]) {
                layer["color_legend_range_count"] = 10;
            }
            if (value === 2 || value === 3) {
                layer["color_legend_data_driven_equal_minmax_data"] = "1";
            }
        }
        updateParent();
        updateDataDrivenColor();
        setUpdate((prevUpdate) => !prevUpdate);
    }

    function handleEqualChange(evt: any) {
        var value = parseInt(evt.target.value);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["color_legend_data_driven_equal_mode"] = value;
            });
        } else {
            layer["color_legend_data_driven_equal_mode"] = value;
        }
        updateParent();
        updateDataDrivenColor();
        setUpdate((prevUpdate) => !prevUpdate);
    }

    function toggleLayerData(newValue: string) {
        if (data) {
            var index = parseInt(layer["color_legend_attribute_index"]);
            if (typeof layer["color_legend_attribute_index"] === "undefined") {
                return;
            }
            //TOVERIFY.
            if (newValue === "1") {
                if (index > -1) {
                    if (typeof data["attribute" + index + "_bound_min"] !== "undefined") {
                        layer["color_legend_range_data_min"] = layer["color_legend_range_min"]
                            ? layer["color_legend_range_min"]
                            : colorRangeMin + "";

                        layer["color_legend_range_min"] = data["attribute" + index + "_bound_min"];
                    }
                    if (typeof data["attribute" + index + "_bound_max"] !== "undefined") {
                        layer["color_legend_range_data_max"] = layer["color_legend_range_max"]
                            ? layer["color_legend_range_max"]
                            : colorRangeMax + "";

                        layer["color_legend_range_max"] = data["attribute" + index + "_bound_max"];
                    }
                } else {
                    layer["color_legend_range_data_min"] = colorRangeMin + "";
                    layer["color_legend_range_data_max"] = colorRangeMax + "";
                }
            } else {
                layer["color_legend_range_max"] = layer["color_legend_range_data_max"]
                    ? layer["color_legend_range_data_max"]
                    : colorRangeMax + "";
                layer["color_legend_range_min"] = layer["color_legend_range_data_min"]
                    ? layer["color_legend_range_data_min"]
                    : colorRangeMin + "";
                delete layer["color_legend_range_data_max"];
                delete layer["color_legend_range_data_min"];
            }
            setNeedsApply(true);
        }
    }

    function openColorGradientMenu() {
        setColorGradientMenu(true);
    }

    function closeColorGradientMenu() {
        setColorGradientMenu(false);
    }

    function setGradient(command: string, value: boolean) {
        const newValue: string = value ? "1" : "0";
        const handleCommand = (layer: any) => {
            switch (command) {
                case "reverse":
                    layer["color_legend_reverse_gradient"] = newValue;
                    break;
                case "interpolate":
                    layer["color_legend_interpolate_gradient"] = newValue;
                    break;
                case "minMaxDynamic":
                    layer["color_legend_data_driven_equal_minmax_dynamic"] = newValue;
                    break;
                case "minMaxData":
                    layer["color_legend_data_driven_equal_minmax_data"] = newValue;
                    toggleLayerData(newValue);
                    break;
            }
        };

        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                handleCommand(currentLayer);
            });
        } else {
            handleCommand(layer);
        }

        updateParent();
        updateDataDrivenColor();
        setUpdate((prevUpdate) => !prevUpdate);
    }

    function setColorRangeMin(min: number, triggerApply?: boolean) {
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["color_legend_range_min"] = min + "";
            });
        } else {
            if (layer["color_legend_range_min"] !== min + "") {
                layer["color_legend_range_min"] = min + "";
            }
        }
        if (triggerApply) {
            if (!makeDirty()) updateDataDrivenColor();
            setUpdate(!update);
        }
    }

    function setColorRangeMax(max: number, triggerApply?: boolean) {
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                if (currentLayer["color_legend_range_max"] !== max + "") {
                    currentLayer["color_legend_range_max"] = max + "";
                }
            });
        } else {
            if (layer["color_legend_range_max"] !== max + "") {
                layer["color_legend_range_max"] = max + "";
            }
        }
        if (triggerApply) {
            if (!makeDirty()) updateDataDrivenColor();
            setUpdate(!update);
        }
    }

    function setColorRangeStep(step: number) {
        if (step < 0.0) step = 0.0;
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["color_legend_range_step"] = step + "";
            });
        } else {
            layer["color_legend_range_step"] = step + "";
        }
        updateDataDrivenColor();
        setUpdate(!update);
    }

    function setColorRangeCount(count: number) {
        if (count < 0) count = 0;
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["color_legend_range_count"] = count;
            });
        } else {
            layer["color_legend_range_count"] = count;
        }
        updateDataDrivenColor();
        setUpdate(!update);
    }

    function setColorGradient(gradientId: string) {
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["color_legend_gradient"] = gradientId ? gradientId : "color";
            });
        } else {
            layer["color_legend_gradient"] = gradientId ? gradientId : "color";
        }
        updateDataDrivenColor();
        setUpdate(!update);
        setColorGradientMenu(false);
    }

    const toggleTwoSided = (evt: any) => {
        var doubleSided = globalSettings[layer.ID]["doubleSided"];
        onDoubleSided(!doubleSided);
    };

    const setLimboValue = (name: string, value: any) => {
        layer[name] = value ? "1" : "0";
        selectedLayers.forEach((currentLayer: any) => {
            currentLayer[name] = value ? "1" : "0";
        });
        setUpdate(!update);
    };

    return (
        <div style={{ paddingTop: 10 }}>
            <div className={common.verticalMiddle} style={{ height: 40 }}>
                <Typography className={common.mediumLabel}>Select Statistical Method</Typography>
            </div>
            <Select value={dataDrivenMethod} className={cx(classes.label, classes.select)} onChange={changeStats}>
                {ColorLegendDataMethods.map((entry: any) => (
                    <MenuItem
                        key={"method-" + entry.value}
                        value={entry.value}
                        className={
                            (!data || histogram.length === 0) && (entry.value === 2 || entry.value === 3)
                                ? common.disabledEvents
                                : ""
                        }
                    >
                        {entry.label}
                    </MenuItem>
                ))}
            </Select>
            <div
                className={classes.row}
                style={{
                    display: dataDrivenMethod === 1 ? "flex" : "none",
                    justifyContent: "flex-start",
                    left: -15,
                    top: 10,
                }}
            >
                <Radio
                    checked={dataDrivenEqualMode === 0}
                    onChange={handleEqualChange}
                    value={0}
                    name="radio-equal-button"
                    size="small"
                />
                Linear
                <Radio
                    checked={dataDrivenEqualMode === 1}
                    onChange={handleEqualChange}
                    value={1}
                    name="radio-equal-button"
                    size="small"
                />
                Logarithmic
            </div>
            <div className={common.verticalMiddle} style={{ height: 42, marginTop: 15 }}>
                <div className={common.verticalMiddle}>
                    <Typography className={cx(common.label, classes.subSectionLabel)} style={{ marginLeft: 0 }}>
                        {doubleSided ? "Front / Outside" : "Configure Colour"}
                    </Typography>
                </div>
                <div className={classes.twoSided}>
                    <Typography className={common.label} style={{ color: "#687684", marginRight: 5 }}>
                        Two sided
                    </Typography>
                    {isMultiple && isLimboState(selectedLayers, "color_legend_double_sided") ? (
                        <Switcher
                            onClick={(evt: any) => {
                                setLimboValue("color_legend_double_sided", true);
                            }}
                        />
                    ) : (
                        <Switch size="small" onChange={toggleTwoSided} color="primary" checked={doubleSided} />
                    )}
                </div>
            </div>
            <div className={classes.row} style={{ marginTop: 15 }}>
                <ColorGradient
                    colorGradientId={colorGradientId}
                    setColorGradient={setColorGradient}
                    openMenu={colorGradientMenu}
                    handleCloseGradientMenu={closeColorGradientMenu}
                    {...props}
                />
            </div>
            <Grid container direction="row" className={classes.sectionGradient} onClick={openColorGradientMenu}>
                <HorizontalColorPalette
                    source={layer}
                    showLabels={true}
                    histogram={histogram}
                    method={dataDrivenMethod}
                    isLimbo={
                        typeof colorRangeMin === "undefined" ||
                        typeof colorRangeMax === "undefined" ||
                        (dataDrivenMethod === 0 && typeof colorRangeStep === "undefined") ||
                        (dataDrivenMethod > 0 && typeof colorRangeCount === "undefined")
                    }
                />
            </Grid>
            <Grid
                container
                direction="row"
                style={{
                    marginBottom: 15,
                    display: data && colorAttributeIndex > -1 ? "inline-flex" : "none",
                }}
            >
                <Histogram
                    width={266}
                    height={90}
                    source={layer}
                    histogram={histogram}
                    method={dataDrivenMethod}
                    showLabels={true}
                    min={colorDataMin}
                    max={colorDataMax}
                />
            </Grid>
            <Grid container>
                <Grid container direction="row">
                    <Grid xs={4} item={true}>
                        <DashInput
                            name={layer.ID + "-minColorLegend"}
                            className={classes.numberInput}
                            value={colorRangeMin}
                            style={{ width: 32, height: 24, left: 9, top: 0 }}
                            onChange={(evt: any) => setColorRangeMin(parseFloat(evt.target.value), true)}
                            isLimbo={isMultiple && isLimboState(selectedLayers, "color_legend_range_min")}
                            disabled={dataDrivenMethod > 1 || minMaxData || minMaxDynamic}
                        />
                        <FormHelperText
                            className={classes.numberHint}
                            disabled={dataDrivenMethod > 1 || minMaxData || minMaxDynamic}
                        >
                            Min.
                        </FormHelperText>
                    </Grid>
                    <span style={{ display: "inline-flex", width: 10, height: 42 }}></span>
                    <Grid xs={4} item={true}>
                        <DashInput
                            name={layer.ID + "-maxColorLegend"}
                            className={classes.numberInput}
                            value={colorRangeMax}
                            style={{ width: 32, height: 24, left: 20, top: 0 }}
                            onChange={(evt: any) => setColorRangeMax(parseFloat(evt.target.value), true)}
                            isLimbo={isMultiple && isLimboState(selectedLayers, "color_legend_range_max")}
                            disabled={dataDrivenMethod > 1 || minMaxData || minMaxDynamic}
                        />
                        <FormHelperText
                            className={classes.numberHint}
                            disabled={dataDrivenMethod > 1 || minMaxData || minMaxDynamic}
                        >
                            Max.
                        </FormHelperText>
                    </Grid>
                    <span
                        style={{
                            display: dataDrivenMethod === 0 ? "block" : "none",
                            width: 10,
                            height: 42,
                        }}
                    ></span>
                    <Grid xs={3} item={true} style={{ display: dataDrivenMethod === 0 ? "block" : "none" }}>
                        <DashInput
                            name={layer.ID + "-stepColorLegend"}
                            className={classes.numberInput}
                            value={colorRangeStep}
                            style={{ width: 32, height: 24, left: 0, top: 0 }}
                            onChange={(evt: any) => setColorRangeStep(parseFloat(evt.target.value))}
                            isLimbo={isMultiple && isLimboState(selectedLayers, "color_legend_range_step")}
                            disabled={interpolateGradient}
                        />
                        <FormHelperText className={classes.numberHint} disabled={interpolateGradient}>
                            Step
                        </FormHelperText>
                    </Grid>
                    <span
                        style={{
                            display: dataDrivenMethod !== 0 ? "block" : "none",
                            width: 10,
                            height: 42,
                        }}
                    ></span>
                    <Grid xs={3} item={true} style={{ display: dataDrivenMethod !== 0 ? "block" : "none" }}>
                        <DashInput
                            name={layer.ID + "-countColorLegend"}
                            className={classes.numberInput}
                            value={colorRangeCount}
                            style={{ width: 32, height: 24, left: 0, top: 0 }}
                            inputProps={{
                                min: 1,
                                max: 100,
                            }}
                            onChange={(evt: any) => setColorRangeCount(parseFloat(evt.target.value))}
                            isLimbo={isMultiple && isLimboState(selectedLayers, "color_legend_range_count")}
                            disabled={interpolateGradient}
                        />
                        <FormHelperText className={classes.numberHint} disabled={interpolateGradient}>
                            Count
                        </FormHelperText>
                    </Grid>
                    <div style={{ display: colorAttributeIndex > -1 ? "block" : "none" }}>
                        <div
                            style={{
                                position: "relative",
                                display: "block",
                            }}
                        >
                            <div className={classes.row} style={{ top: 5, left: -14, display: "none" }}>
                                <Checkbox
                                    size="small"
                                    checked={minMaxDynamic}
                                    onChange={(evt: any) => setGradient("minMaxDynamic", evt.target.checked)}
                                    indeterminate={isLimboState(
                                        selectedLayers,
                                        "color_legend_data_driven_equal_minmax_dynamic"
                                    )}
                                />
                                <Typography className={classes.label}>Min/max dynamic limits</Typography>
                            </div>
                            <div
                                className={cx(classes.row, !data ? common.disabledEvents : "")}
                                style={{ top: 5, left: -14 }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={minMaxData}
                                    onChange={(evt: any) => setGradient("minMaxData", evt.target.checked)}
                                    indeterminate={isLimboState(
                                        selectedLayers,
                                        "color_legend_data_driven_equal_minmax_data"
                                    )}
                                    disabled={dataDrivenMethod === 2 || dataDrivenMethod === 3}
                                />
                                <Typography className={classes.label}>Min/max from data</Typography>
                            </div>
                        </div>
                        <div className={classes.row} style={{ top: 5, left: -14 }}>
                            <Checkbox
                                size="small"
                                checked={reverseGradient}
                                onChange={(evt: any) => setGradient("reverse", evt.target.checked)}
                                indeterminate={isLimboState(selectedLayers, "color_legend_reverse_gradient")}
                            />
                            <Typography className={classes.label}>Reverse Gradient</Typography>
                        </div>
                        <div className={classes.row} style={{ top: 5, left: -14 }}>
                            <Checkbox
                                size="small"
                                checked={interpolateGradient}
                                onChange={(evt: any) => setGradient("interpolate", evt.target.checked)}
                                indeterminate={isLimboState(selectedLayers, "color_legend_interpolate_gradient")}
                            />
                            <Typography className={classes.label}>Interpolate (continous) colours</Typography>
                        </div>
                    </div>
                </Grid>
            </Grid>
            <div className={common.verticalMiddle} style={{ display: doubleSided ? "flex" : "none" }}>
                <Typography className={cx(common.label, classes.subSectionLabel)} style={{ marginLeft: 0 }}>
                    {doubleSided ? "Back / Inside" : ""}
                </Typography>
            </div>
            <ColorPalette
                selectedColor={backColor}
                side="back"
                doubleSided={doubleSided}
                onColorClick={onColorClick}
                onDoubleSided={onDoubleSided}
                style={{
                    display: doubleSided ? "block" : "none",
                }}
            />
        </div>
    );
}

function AlphaAttributes(props: IExtended) {
    const { classes } = useStylesColorLegend();
    const { classes: common } = commonStyles();

    const { layer, selectedLayers, updateDataDrivenColor, updateParent, globalSettings } = props;
    const { alphaColorGradientId, reverseAlphaGradient, colorAttribute } = globalSettings[layer.ID];

    const [colorGradientMenu, setColorGradientMenu] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectedColor, setSelectedColor] = useState("");
    const [update, setUpdate] = useState(false);
    const [values, setValues] = useState([]);
    const [gradient, setAlphaGradient] = useState<any>([]);

    useEffect(() => {
        var values = [];
        const attributes = getAttributesObjects(layer.ID);
        const attribute = attributes.find((attribute: any) => attribute.name === colorAttribute);

        if (attribute && attribute.values) {
            values = attribute.values;
        }
        setValues(values);
    }, [colorAttribute, layer.ID]);

    useEffect(() => {
        layer["alpha_attribute"] = "1";
        GradientActions.useAttributeGradient = true;
        if (!GradientActions.attributeGradient[layer.ID]) {
            GradientActions.attributeGradient[layer.ID] = [];
        }
        if (colorAttribute && colorAttribute !== "") {
            var gradient: any = [];
            let gradientId = layer["color_legend_alpha_gradient"];
            if (gradientId !== "custom") {
                gradient = GradientActions.getAlphaGradient(layer, values);
            } else {
                gradient = GradientActions.customGradient;
            }
            setAlphaGradient([...gradient]);
            GradientActions.attributeGradient[layer.ID][colorAttribute] = Utils.rgbToHSL(gradient);
        }
    }, [layer, colorAttribute, alphaColorGradientId, values, update]);

    const setGradient = (command: string, value: boolean) => {
        const newValue: string = value ? "1" : "0";
        switch (command) {
            case "reverseAlpha":
                layer["color_legend_reverse_alpha_gradient"] = newValue;
                break;
            default:
                console.log("Unhandled AlphaGradient Command!! ", command);
                break;
        }

        updateDataDrivenColor();
        updateParent();
        setUpdate((prevUpdate) => !prevUpdate);
    };

    function openColorGradientMenu() {
        setColorGradientMenu(true);
    }

    function closeColorGradientMenu() {
        setColorGradientMenu(false);
    }
    const changeColor = (event: any, index: number) => {
        setAnchorEl(event.target);
        setSelectedIndex(index);
        updateParent();
    };

    const handleColorChange = (color: any) => {
        console.log("AlphaAttributes.color:", color);
        if (selectedIndex === -1) {
            return;
        }
        var pickedColor: any = color.rgb;
        var hex = color.hex;
        const newGradient: any = [...gradient];
        newGradient[selectedIndex] = "rgb(" + pickedColor.r + "," + pickedColor.g + "," + pickedColor.b + ")";
        setAlphaGradient(newGradient);
        setSelectedColor(hex);
        GradientActions.customGradient = newGradient;
        setColorGradient("custom");
        layer["color_legend_alpha_gradient"] = "custom";
        updateDataDrivenColor();
    };

    const setColorGradient = (gradientId: string) => {
        layer["color_legend_alpha_gradient"] = gradientId;
        /*
        if (gradientId !== "custom") {
            GradientActions.attributeGradient[layer.ID][colorAttribute] = GradientActions.getAttributeGradient(layer);
        } else {
            GradientActions.customGradient = gradient;
            if (colorAttribute && colorAttribute !== "") {
                GradientActions.attributeGradient[layer.ID][colorAttribute] = Utils.rgbToHSL(gradient);
            }
        }
        */
        setUpdate(!update);
        setColorGradientMenu(false);
        updateDataDrivenColor();
    };

    const getColor = (index: number) => {
        if (gradient && gradient[index]) {
            return gradient[index];
        }
        return "#CECECE";
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedIndex(-1);
        setSelectedColor("");
    };

    return (
        <div style={{ paddingTop: 10 }}>
            <div className={classes.row}>
                <Typography className={classes.subSectionLabel}>Configure Colour Gradient</Typography>
            </div>
            <div className={classes.row}>
                <Typography className={classes.subSectionLabel}>Spectral Colour Ramp</Typography>
            </div>
            <ColorGradient
                colorGradientId={alphaColorGradientId}
                setColorGradient={setColorGradient}
                openMenu={colorGradientMenu}
                handleCloseGradientMenu={closeColorGradientMenu}
                {...props}
            />
            <Grid container direction="row" className={classes.sectionGradient} onClick={openColorGradientMenu}>
                {alphaColorGradientId === "custom" ? (
                    <div
                        className={common.verticalMiddleCenter}
                        style={{ width: "100%", height: 26, backgroundColor: "#E9EBED" }}
                    >
                        <Typography className={common.mediumLabel} style={{ color: "#687684" }}>
                            � Custom Colour Ramp �
                        </Typography>
                    </div>
                ) : (
                    <HorizontalColorPalette source={layer} showLabels={false} isAlpha={true} />
                )}
            </Grid>
            <div className={classes.row} style={{ top: 5, left: -14 }}>
                <Checkbox
                    size="small"
                    checked={reverseAlphaGradient}
                    onChange={(evt: any) => setGradient("reverseAlpha", evt.target.checked)}
                    indeterminate={isLimboState(selectedLayers, "color_legend_reverse_alpha_gradient")}
                />
                <Typography className={common.label}>Reverse Gradient</Typography>
            </div>
            <Fragment>
                {values.map((value: string, index: number) =>
                    value !== "" ? (
                        <div
                            key={"color-" + index}
                            className={common.verticalMiddle}
                            style={{
                                position: "relative",
                                height: 34,
                                width: "100%",
                                borderBottom: index < values.length - 1 ? "solid  0.65px #E9EBED" : 0,
                            }}
                        >
                            <div style={{ paddingLeft: 16 }}>
                                <Typography className={common.mediumLabel}>{value}</Typography>
                            </div>

                            <div
                                style={{
                                    position: "absolute",
                                    right: 10,
                                    width: 26,
                                    height: 26,
                                    cursor: "pointer",
                                    backgroundColor: getColor(index),
                                }}
                                onClick={(evt) => changeColor(evt, index)}
                            ></div>
                        </div>
                    ) : (
                        ""
                    )
                )}
            </Fragment>
            {anchorEl !== null ? (
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                    MenuListProps={{
                        style: {
                            width: 270,
                            maxHeight: 270,
                            overflow: "hidden",
                        },
                    }}
                >
                    <SketchPicker
                        color={selectedColor}
                        className={classes.picker}
                        onChangeComplete={handleColorChange}
                    />
                </Menu>
            ) : (
                ""
            )}
        </div>
    );
}

export default function ColorLegend(props: IColorLegend) {
    const { classes, cx } = useStylesColorLegend();
    const { classes: common } = commonStyles();
    let layer: any = props["layer"];
    const { selectedLayers, updateParent } = props;

    const isBlockModel = GlobalActions.checkBlockModel(layer);

    const isMultiple = selectedLayers.length > 1;
    let not_selectable = Boolean(layer.not_selectable);
    let useParent = layer["use_parent"] === "1";

    if (isMultiple) {
        var commonLayer = getCommonAttributeValue(selectedLayers);
        layer = { ...layer, ...commonLayer };
        not_selectable = Boolean(layer.not_selectable);
        var initialState = getGlobalSettings(commonLayer);
        globalSettings[layer.ID] = initialState;
        if (!layer.hasOwnProperty("use_parent")) {
        }
    } else {
        globalSettings[layer.ID] = getColorLegendSettings(layer);
    }

    //console.log("globalSettings:", globalSettings);
    const attributes = getAttributesObjects(layer.ID).filter(
        (attribute: string) => attribute !== "voxelfarm_attribute"
    );

    //console.log("attributes:", attributes);
    let histogram: any = [];
    var indeterminate = false;

    const {
        colorRangeMin,
        colorRangeMax,
        colorRangeStep,
        colorRangeCount,
        colorAttribute,
        colorAttributeIndex,
        colorMode,
        colorStaticB,
        colorStaticG,
        colorStaticR,
        colorStaticBackB,
        colorStaticBackG,
        colorStaticBackR,
        doubleSided,
        alphaColorGradientId,
    } = globalSettings[layer.ID];

    var frontColor = "#808080",
        backColor = "#808080";
    if (!isNaN(colorStaticR) && !isNaN(colorStaticG) && !isNaN(colorStaticB)) {
        frontColor = Utils.rgbToHex(colorStaticR, colorStaticG, colorStaticB);
    }
    if (!isNaN(colorStaticBackR) && !isNaN(colorStaticBackG) && !isNaN(colorStaticBackB)) {
        backColor = Utils.rgbToHex(colorStaticBackR, colorStaticBackG, colorStaticBackB);
    }
    const entityId = GlobalActions.getEntityId(layer);
    const data: any = ViewerActions.extendedMeta[entityId];

    if (data && colorAttributeIndex > -1) {
        //console.log("data exists:", data);
        histogram = GradientActions.getHistogram(layer.ID, colorAttributeIndex);
        //console.log("histogram:", histogram);
    }
    const [update, setUpdate] = useState(false);
    const [needsApply, setNeedsApply] = useState(false);
    const [isAlpha, setIsAlpha] = useState(false);

    useEffect(() => {
        if (colorAttribute !== "") {
            var curAttr = attributes.find((curAttribute: any) => curAttribute.name === colorAttribute);
            if (curAttr && curAttr.values) {
                setIsAlpha(true);
                layer["alpha_attribute"] = "1";
            } else {
                setIsAlpha(false);
            }
        }
    }, [attributes, layer, colorAttribute]);

    function updateStaticColor() {
        var updateCurrentMesh = (layer: any) => {
            layer["use_parent"] = "0";
            layer["color_legend_mode"] = "1";
            var meshProperty = getMeshProperties(layer);
            var newProp = {
                ...meshProperty,
                meshUseParentColorLegend: false,
            };
            updateMeshProperties(layer, newProp, () => {
                updateParent();
            });
        };
        var setStaticColor = (layer: any) => {
            if (not_selectable) {
                updateCurrentMesh(layer);
            } else {
                var colorLegendData = {
                    doubleSided: doubleSided,
                    front: {
                        r: colorStaticR,
                        g: colorStaticG,
                        b: colorStaticB,
                    },
                    back: {
                        r: colorStaticBackR,
                        g: colorStaticBackG,
                        b: colorStaticBackB,
                    },
                };
                handleLayerEvent("setStaticColor", colorLegendData, layer);
            }
        };
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                setStaticColor(currentLayer);
            });
        } else {
            setStaticColor(layer);
        }
        updateParent();
        setUpdate((prevUpdate) => !prevUpdate);
    }

    const updateDataDrivenColor = (holdDispatch?: boolean) => {
        const dispatchDataDrivenEvent = (layer: any) => {
            var colorLegendData = {
                statisticalMethodType: globalSettings[layer.ID]["dataDrivenMethod"],
                statisticalIntervalType: globalSettings[layer.ID]["dataDrivenEqualMode"],
                colorAttribute: globalSettings[layer.ID]["colorAttribute"],
                colorAttributeIndex: globalSettings[layer.ID]["colorAttributeIndex"],
                rangeMin: globalSettings[layer.ID]["colorRangeMin"],
                rangeMax: globalSettings[layer.ID]["colorRangeMax"],
                rangeStep: Math.max(globalSettings[layer.ID]["colorRangeStep"], 0),
                rangeCount: Math.max(globalSettings[layer.ID]["colorRangeCount"], 0),
                colorReverseGradient: globalSettings[layer.ID]["isAlpha"]
                    ? globalSettings[layer.ID]["reverseAlphaGradient"]
                    : globalSettings[layer.ID]["reverseGradient"],
                colorInterpolateContinuous: globalSettings[layer.ID]["interpolateGradient"],
                gradientId: globalSettings[layer.ID]["isAlpha"]
                    ? globalSettings[layer.ID]["alphaColorGradientId"]
                    : globalSettings[layer.ID]["colorGradientId"],
                distribution: GradientActions.getDistribution(histogram, layer),
                doubleSided: globalSettings[layer.ID]["doubleSided"],
                front: {
                    r: globalSettings[layer.ID]["colorStaticR"],
                    g: globalSettings[layer.ID]["colorStaticG"],
                    b: globalSettings[layer.ID]["colorStaticB"],
                },
                back: {
                    r: globalSettings[layer.ID]["colorStaticBackR"],
                    g: globalSettings[layer.ID]["colorStaticBackG"],
                    b: globalSettings[layer.ID]["colorStaticBackB"],
                },
                useAttributeGradient: globalSettings[layer.ID]["useAttributeGradient"],
                attributeGradient: globalSettings[layer.ID]["attributeGradient"],
            };
            console.log("colorLegendData:", colorLegendData);
            handleLayerEvent("setDataDrivenColor", colorLegendData, layer);
        };

        const setDataDrivenColor = (layer: any) => {
            layer["color_legend_mode"] = "2";
        };
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                setDataDrivenColor(currentLayer);
                globalSettings[currentLayer.ID] = getColorLegendSettings(currentLayer);
                dispatchDataDrivenEvent(currentLayer);
            });
        }
        globalSettings[layer.ID] = getColorLegendSettings(layer);
        setDataDrivenColor(layer);
        if (!holdDispatch && !needsApply) {
            dispatchDataDrivenEvent(layer);
        }
        setUpdate((prevUpdate) => !prevUpdate);
        updateParent();
    };

    function setColorMode(mode: number) {
        layer["color_legend_mode"] = mode.toString();
        globalSettings[layer.ID]["colorMode"] = mode;
        if (isMultiple) {
            if (mode === 0 && not_selectable) {
                layer["use_parent"] = "1";
                delete layer["color_legend_mode"];
            }
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["colorMode"] = mode;
                if (mode === 0 && not_selectable) {
                    currentLayer["use_parent"] = "1";
                    delete currentLayer["color_legend_mode"];
                }
            });
        }
        setUpdate(!update);
    }

    function setFlatColor() {
        setColorMode(1);
        updateStaticColor();
    }

    function setDataDrivenColor() {
        setColorMode(2);
        updateDataDrivenColor();
    }

    function isColor(color: any) {
        return color.rgb.r === colorStaticR && color.rgb.g === colorStaticG && color.rgb.b === colorStaticB;
    }

    function onStaticColorClick(color: any) {
        const updateLayer = (layer: any) => {
            layer["color_legend_mode"] = "1";
            layer["color_legend_static_r"] = color.rgb.r;
            layer["color_legend_static_g"] = color.rgb.g;
            layer["color_legend_static_b"] = color.rgb.b;
        };

        var updateCurrentMesh = (layer: any, color: any) => {
            layer.use_parent = "0";
            var meshProperty = getMeshProperties(layer);
            var newProp = {
                ...meshProperty,
                meshUseParentColorLegend: false,
                meshStaticColorFront: [color.rgb.r / 255.0, color.rgb.g / 255.0, color.rgb.b / 255.0],
            };
            updateMeshProperties(layer, newProp);
        };
        var setStaticColor = (layer: any, color: any) => {
            updateLayer(layer);
            if (not_selectable) {
                updateCurrentMesh(layer, color);
            }

            globalSettings[layer.ID]["colorStaticR"] = color.rgb.r;
            globalSettings[layer.ID]["colorStaticG"] = color.rgb.g;
            globalSettings[layer.ID]["colorStaticB"] = color.rgb.b;

            var colorLegendData = {
                doubleSided: globalSettings[layer.ID]["doubleSided"],
                front: {
                    r: globalSettings[layer.ID]["colorStaticR"],
                    g: globalSettings[layer.ID]["colorStaticG"],
                    b: globalSettings[layer.ID]["colorStaticB"],
                },
                back: {
                    r: globalSettings[layer.ID]["colorStaticBackR"],
                    g: globalSettings[layer.ID]["colorStaticBackG"],
                    b: globalSettings[layer.ID]["colorStaticBackB"],
                },
            };
            handleLayerEvent("setStaticColor", colorLegendData, layer);
        };

        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                setStaticColor(currentLayer, color);
            });
        } else {
            setStaticColor(layer, color);
        }
        setUpdate((prevUpdate) => !prevUpdate);
        updateParent();
    }

    function setUseParent(value: boolean) {
        layer["use_parent"] = value ? "1" : "0";
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["use_parent"] = value ? "1" : "0";
                var meshProperty = getMeshProperties(currentLayer);
                var newProp = {
                    ...meshProperty,
                    meshUseParentColorLegend: value,
                };
                updateMeshProperties(currentLayer, newProp);
            });
        } else {
            var meshProperty = getMeshProperties(layer);
            var newProp = {
                ...meshProperty,
                meshUseParentColorLegend: value,
            };
            updateMeshProperties(layer, newProp);
        }
        if (value) {
            setColorMode(0);
        } else {
            setColorMode(1);
            updateStaticColor();
        }
        setUpdate((prevUpdate) => !prevUpdate);
        updateParent();
    }

    function selectColorAttribute(curAttribute: any) {
        var value = curAttribute.name;
        var index = curAttribute.index;
        var isAlpha = Boolean(curAttribute.values);
        if (value === "") {
            return;
        }

        selectedLayers.forEach((currentLayer: any) => {
            currentLayer["alpha_attribute"] = isAlpha ? "1" : "0";
            currentLayer["color_legend_attribute"] = value;
            currentLayer["color_legend_attribute_index"] = index;
            currentLayer["color_legend_range_min"] = colorRangeMin + "";
            currentLayer["color_legend_range_max"] = colorRangeMax + "";
            currentLayer["color_legend_range_step"] = colorRangeStep + "";
            currentLayer["color_legend_range_count"] = colorRangeCount + "";
        });

        if (isAlpha) {
            GradientActions.useAttributeGradient = true;
            if (alphaColorGradientId !== "custom") {
                if (!GradientActions.attributeGradient[layer.ID]) {
                    GradientActions.attributeGradient[layer.ID] = [];
                }
                GradientActions.attributeGradient[layer.ID][value] = GradientActions.getAttributeGradient(layer);
            }
        }
        setIsAlpha(isAlpha);
        setUpdate((preUpdate) => !preUpdate);
        if (isBlockModel) {
            setNeedsApply(true);
        } else {
            updateDataDrivenColor();
        }
    }

    function getLabel(colorAttribute: string) {
        let label = colorAttribute;
        attributes.forEach((attribute: any) => {
            if (attribute.name === colorAttribute) {
                label = attribute.label;
            }
        });
        return label;
    }

    function applySettings() {
        handleLayerEvent("refreshView", null, null);
        setNeedsApply(false);
    }

    const onDoubleSided = (enabled: boolean) => {
        layer["color_legend_double_sided"] = enabled ? "1" : "0";
        globalSettings[layer.ID]["doubleSided"] = enabled;
        if (colorMode === 1) {
            updateStaticColor();
        } else {
            updateDataDrivenColor();
        }
    };

    const onColorClick = (side: string, color: any) => {
        console.log("side:", side, "|color:", color);
        if (side === "front") {
            layer["color_legend_static_r"] = color.rgb.r + "";
            layer["color_legend_static_g"] = color.rgb.g + "";
            layer["color_legend_static_b"] = color.rgb.b + "";
        } else {
            layer["color_legend_static_back_r"] = color.rgb.r + "";
            layer["color_legend_static_back_g"] = color.rgb.g + "";
            layer["color_legend_static_back_b"] = color.rgb.b + "";
        }
        if (colorMode === 1) {
            updateStaticColor();
        } else {
            updateDataDrivenColor();
        }
    };

    const handleTabChange = (e: any, newValue: number) => {
        if (newValue === 1) {
            setFlatColor();
        } else {
            setDataDrivenColor();
        }
    };

    return (
        <Section name="colorLegend" label="Color">
            <div className={common.sectionContent}>
                {not_selectable ? (
                    <div className={classes.row}>
                        <Checkbox
                            size="small"
                            checked={useParent}
                            onChange={(e) => setUseParent(e.target.checked)}
                            indeterminate={indeterminate}
                        />
                        <Typography className={common.label}>Use Parent Color</Typography>
                    </div>
                ) : (
                    ""
                )}
                <div className={classes.modeSelector} style={{ display: colorMode === 0 ? "none" : "block" }}>
                    <Tabs
                        value={colorMode}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        className={common.tabs}
                    >
                        <Tab value={1} label="Constant" className={common.tab} />
                        <Tab value={2} label="Gradient" className={common.tab} />
                    </Tabs>
                    <div
                        id="staticColorContainer"
                        className={classes.subSectionContainer}
                        style={colorMode === 1 ? { display: "block" } : { display: "none" }}
                    >
                        <Typography className={classes.subSectionLabel}>Pick Color</Typography>
                        <div className={classes.colorContainer}>
                            {DefaultColorPalette.map((color: any, index: number) => (
                                <div
                                    key={index}
                                    className={classes.staticColorBlock}
                                    style={{
                                        backgroundColor: color.hexString,
                                        display: "inline-block",
                                        width: 16,
                                        height: 16,
                                        border: isColor(color) ? "solid 3px navy" : "none",
                                    }}
                                    onClick={(evt) => {
                                        onStaticColorClick(color);
                                    }}
                                ></div>
                            ))}
                        </div>
                    </div>
                    <div
                        id="dataDrivenColorContainer"
                        className={classes.subSectionContainer}
                        style={colorMode === 2 && !not_selectable ? { display: "block" } : { display: "none" }}
                    >
                        <div className={classes.row} style={{ height: 40, paddingTop: 15 }}>
                            <Typography className={cx(common.middle, classes.subSectionLabel)}>
                                Select Attribute
                            </Typography>
                        </div>
                        <div className={common.middleCenter} style={{ height: 35, width: "100%" }}>
                            <CustomSelect
                                inputProps={{ style: { width: "100%" } }}
                                menuListProps={{ style: { width: 280 } }}
                                value={
                                    colorAttribute && colorAttribute.length > 0
                                        ? isMultiple && isLimboState(selectedLayers, "color_legend_attribute")
                                            ? "Multi"
                                            : getLabel(colorAttribute)
                                        : isMultiple && isLimboState(selectedLayers, "color_legend_attribute")
                                        ? "Multi"
                                        : "Default"
                                }
                                className={common.customSelect}
                            >
                                {!colorAttribute || colorAttribute.length === 0 ? (
                                    <MenuItem value="Default" onClick={(e: any) => selectColorAttribute("")}>
                                        Default
                                    </MenuItem>
                                ) : (
                                    ""
                                )}
                                <MenuItem value="Multi" style={{ display: "none" }} disabled>
                                    Multi
                                </MenuItem>
                                {attributes.map((attribute: any) => {
                                    const name: string = attribute.name;
                                    if (name && name.toLowerCase() !== "voxelfarm_attribute") {
                                        return (
                                            <MenuItem
                                                key={"attribute-" + attribute.index + "-" + attribute.name}
                                                value={attribute.name}
                                                onClick={() => selectColorAttribute(attribute)}
                                            >
                                                {attribute.label}
                                            </MenuItem>
                                        );
                                    }
                                    return "";
                                })}
                            </CustomSelect>
                        </div>
                        {colorAttribute !== "" ? (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {isAlpha ? (
                                    <AlphaAttributes
                                        {...props}
                                        setNeedsApply={setNeedsApply}
                                        updateDataDrivenColor={updateDataDrivenColor}
                                        globalSettings={globalSettings}
                                    />
                                ) : (
                                    <NumericAttributes
                                        {...props}
                                        setNeedsApply={setNeedsApply}
                                        updateDataDrivenColor={updateDataDrivenColor}
                                        globalSettings={globalSettings}
                                        histogram={histogram}
                                        onDoubleSided={onDoubleSided}
                                        onColorClick={onColorClick}
                                        frontColor={frontColor}
                                        backColor={backColor}
                                    />
                                )}
                            </div>
                        ) : (
                            <NumericAttributes
                                {...props}
                                setNeedsApply={setNeedsApply}
                                updateDataDrivenColor={updateDataDrivenColor}
                                globalSettings={globalSettings}
                                histogram={histogram}
                                onDoubleSided={onDoubleSided}
                                onColorClick={onColorClick}
                                frontColor={frontColor}
                                backColor={backColor}
                            />
                        )}
                        {needsApply ? (
                            <div className={classes.bottomButtonContainer}>
                                <Button
                                    onClick={applySettings}
                                    title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                                >
                                    Apply
                                </Button>
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            </div>
        </Section>
    );
}
